window.theme = window.theme || {};
window.Shopify = window.Shopify || {};

theme.config = {
  hasSessionStorage: true,
  hasLocalStorage: true,
  mediaQuerySmall: 'screen and (max-width: 767px)',
  motionReduced: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  isTouch: 'ontouchstart' in window || navigator.MaxTouchPoints > 0 || navigator.msMaxTouchPoints > 0,
  extractedPopover: null,
  resizeTimer: 0,
  lastResizeTime: 0,
  initialResizeHandled: false,
  splitTextInstance: [],
  // rtl: document.documentElement.getAttribute('dir') === 'rtl' ? true : false,
};

(function () {
  /*==================================
    Things that require DOM to be readys
  =====================================*/
  theme.DOMready = function (callback) {
    if (document.readyState != 'loading') callback();
    else document.addEventListener('DOMContentLoaded', callback);
  };

  theme.utils = {
    throttle: (callback) => {
      let requestId = null,
        lastArgs;
      const later = (context) => () => {
        requestId = null;
        callback.apply(context, lastArgs);
      };
      const throttled = (...args) => {
        lastArgs = args;
        if (requestId === null) {
          requestId = requestAnimationFrame(later(this));
        }
      };
      throttled.cancel = () => {
        cancelAnimationFrame(requestId);
        requestId = null;
      };
      return throttled;
    },

    debounce: (fn, wait) => {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), wait);
      };
    },

    waitForEvent: (element, eventName) => {
      return new Promise((resolve) => {
        const done = (event) => {
          if (event.target === element) {
            element.removeEventListener(eventName, done);
            resolve(event);
          }
        };
        element.addEventListener(eventName, done);
      });
    },

    fetchConfig: (type = 'json') => {
      return {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
      };
    },

    postLink: (path, options) => {
      options = options || {};
      const method = options['method'] || 'post';
      const params = options['parameters'] || {};

      const form = document.createElement('form');
      form.setAttribute('method', method);
      form.setAttribute('action', path);

      for (const key in params) {
        const hiddenField = document.createElement('input');
        hiddenField.setAttribute('type', 'hidden');
        hiddenField.setAttribute('name', key);
        hiddenField.setAttribute('value', params[key]);
        form.appendChild(hiddenField);
      }
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },

    sectionId: (element) => {
      if (element.hasAttribute('data-section-id')) return element.dataset.sectionId;

      element = element.classList.contains('shopify-section') ? element : element.closest('.shopify-section');
      return element.id.replace('shopify-section-', '');
    },

    imageLoaded: (imageOrArray) => {
      if (!imageOrArray) {
        return Promise.resolve();
      }
      imageOrArray = imageOrArray instanceof Element ? [imageOrArray] : Array.from(imageOrArray);
      return Promise.all(
        imageOrArray.map((image) => {
          return new Promise((resolve) => {
            if ((image.tagName === 'IMG' && image.complete) || !image.offsetParent) {
              resolve();
            } else {
              image.onload = () => resolve();
            }
          });
        })
      );
    },

    visibleMedia: (media) => {
      return Array.from(media).find((item) => {
        const style = window.getComputedStyle(item);
        return style.display !== 'none';
      });
    },

    setScrollbarWidth: () => {
      const scrollbarWidth = window.innerWidth - document.body.clientWidth;
      if (scrollbarWidth > 0) {
        document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
      }
    },
  };

  theme.Currency = {
    formatMoney: (cents, format = '') => {
      if (typeof cents === 'string') {
        cents = cents.replace('.', '');
      }
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/,
        formatString = format || window.themeVariables.settings.moneyFormat;
      function defaultTo(value2, defaultValue) {
        return value2 == null || value2 !== value2 ? defaultValue : value2;
      }
      function formatWithDelimiters(number, precision, thousands, decimal) {
        precision = defaultTo(precision, 2);
        thousands = defaultTo(thousands, ',');
        decimal = defaultTo(decimal, '.');
        if (isNaN(number) || number == null) {
          return 0;
        }
        number = (number / 100).toFixed(precision);
        let parts = number.split('.'),
          dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
          centsAmount = parts[1] ? decimal + parts[1] : '';
        return dollarsAmount + centsAmount;
      }

      function formatWithDelimitersRR(number, precision, thousands, decimal) {
        precision = defaultTo(precision, 2);
        thousands = defaultTo(thousands, ',');
        decimal = defaultTo(decimal, '.');
        if (isNaN(number) || number == null) {
          return 0;
        }
        // Convert the number to a fixed precision
        number = number.toFixed(precision);
        // Split into dollars and cents
        let parts = number.split('.');
        // Format the dollars part with a custom thousands separator
        let dollarsAmount = parts[0].replace(/(\d{2})(\d{3})/, '$1' + thousands + '$2');
        // Add the cents part
        let centsAmount = parts[1] ? decimal + parts[1] : '';
        return dollarsAmount + centsAmount;
      }
      let value = '';
      switch (formatString.match(placeholderRegex)[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_space_separator':
          value = formatWithDelimiters(cents, 2, ' ', '.');
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 2, "'", '.');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = formatWithDelimiters(cents, 0, ' ');
          break;
        case 'amount_no_decimals_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 0, "'");
          break;
        default:
          value = formatWithDelimiters(cents, 2);
          break;
      }
      if (formatString.indexOf('with_comma_separator') !== -1) {
        return formatString.replace(placeholderRegex, value);
      } else {
        return formatString.replace(placeholderRegex, value);
      }
    },
  };

  (theme.pubsub = {
    PUB_SUB_EVENTS: {
      cartUpdate: 'cartUpdate',
      quantityUpdate: 'quantityUpdate',
      variantChange: 'variantChange',
      cartError: 'cartError',
      facetUpdate: 'facetUpdate',
      quantityRules: 'quantityRules',
      quantityBoundries: 'quantityBoundries',
      optionValueSelectionChange: 'optionValueSelectionChange',
    },
    subscribers: {},
    subscribe: (eventName, callback) => (
      theme.pubsub.subscribers[eventName] === void 0 && (theme.pubsub.subscribers[eventName] = []),
      (theme.pubsub.subscribers[eventName] = [...theme.pubsub.subscribers[eventName], callback]),
      function () {
        theme.pubsub.subscribers[eventName] = theme.pubsub.subscribers[eventName].filter((cb) => cb !== callback);
      }
    ),
    publish: (eventName, data) => {
      theme.pubsub.subscribers[eventName] &&
        theme.pubsub.subscribers[eventName].forEach((callback) => {
          callback(data);
        });
    },
  }),
    (theme.cookiesEnabled = function () {
      let cookieEnabled = navigator.cookieEnabled;
      return (
        cookieEnabled ||
          ((document.cookie = 'testcookie'), (cookieEnabled = document.cookie.indexOf('testcookie') !== -1)),
        cookieEnabled
      );
    });

  theme.SwiperSlide = function (element, properties) {
    const _sectionSwiper = new Swiper(element, properties);
    //const _sectionSwiperInit = _sectionSwiper.init();
    return _sectionSwiper;
  };

  theme.MagnetEffect = (function () {
    class MagnetEffect {
      constructor(container, magnetStrength = 0.15) {
        this.container = container;
        this.child = this.container.children[0]; // Cache reference to child
        this.magnetStrength = magnetStrength; // Similar to "this.magnet" in the Motion example
        this.animationFrame = null; // For canceling animations
        this.currentX = 0; // Store current X position
        this.currentY = 0; // Store current Y position
        this.targetX = 0; // Target X position for animation
        this.targetY = 0; // Target Y position for animation
      }

      load() {
        this.addEventListeners();
      }

      addEventListeners() {
        this.container.addEventListener('mousemove', this.handleMouseMove.bind(this));
        this.container.addEventListener('mouseleave', this.handleMouseLeave.bind(this));
      }

      handleMouseMove(e) {
        const pos = this.container.getBoundingClientRect();
        const mx = (e.clientX - pos.left) / this.container.offsetWidth - 0.5;
        const my = (e.clientY - pos.top) / this.container.offsetHeight - 0.5;

        this.targetX = mx * this.magnetStrength * this.container.offsetWidth;
        this.targetY = my * this.magnetStrength * this.container.offsetHeight;

        this.animate(); // Start the animation
      }

      handleMouseLeave() {
        this.targetX = 0;
        this.targetY = 0;

        this.animate(); // Reset animation
      }

      animate() {
        if (this.animationFrame) cancelAnimationFrame(this.animationFrame);

        const easing = 0.15; // Easing factor (spring-like effect)
        const dx = this.targetX - this.currentX;
        const dy = this.targetY - this.currentY;

        this.currentX += dx * easing;
        this.currentY += dy * easing;

        this.container.style.transform = `translate(${this.currentX}px, ${this.currentY}px)`;
        this.child.style.transform = `translate(${this.currentX * 0.2}px, ${this.currentY * 0.2}px)`; // Slightly reduced for child

        // Continue animation if not yet settled
        if (Math.abs(dx) > 0.1 || Math.abs(dy) > 0.1) {
          this.animationFrame = requestAnimationFrame(this.animate.bind(this));
        }
      }
    }

    return MagnetEffect;
  })();

  // Delay JavaScript until user interaction
  theme.initWhenVisible = (function () {
    class ScriptLoader {
      constructor(callback, delay = 5000) {
        this.loadScriptTimer = setTimeout(callback, delay);
        this.userInteractionEvents = [
          'mouseover',
          'mousemove',
          'keydown',
          'touchstart',
          'touchend',
          'touchmove',
          'wheel',
        ];

        this.onScriptLoader = this.triggerScriptLoader.bind(this, callback);
        this.userInteractionEvents.forEach((event) => {
          window.addEventListener(event, this.onScriptLoader, {
            passive: !0,
          });
        });
      }

      triggerScriptLoader(callback) {
        callback();
        clearTimeout(this.loadScriptTimer);
        this.userInteractionEvents.forEach((event) => {
          window.removeEventListener(event, this.onScriptLoader, {
            passive: !0,
          });
        });
      }
    }

    return ScriptLoader;
  })();
})();
